import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Plus, Trash2, RefreshCw, Info } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

const ENC_OPTS = [
  { value: "tls", label: "TLS (STARTTLS, port 587)" },
  { value: "ssl", label: "SSL (port 465)" },
  { value: "none", label: "None (no encryption)" },
];

const empty = { provider: "smtp", enabled: false, host: "", port: 587, encryption: "tls", username: "", password: "",
  from_name: "", from_email: "", reply_to: "", default_cc: "", password_set: false,
  m365: { tenant_id: "", client_id: "", client_secret: "", client_secret_set: false, sender_email: "", sender_name: "", reply_to: "", complete: false } };

export default function EmailConfig() {
  const [form, setForm] = useState(empty);
  const [placeholders, setPlaceholders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTpl, setActiveTpl] = useState(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setM = (k, v) => setForm((f) => ({ ...f, m365: { ...f.m365, [k]: v } }));

  const load = useCallback(() => {
    api.get("/admin/email/config").then((r) => {
      const { placeholders: ph, templates: tps, m365, ...cfg } = r.data;
      setForm({ ...empty, ...cfg, password: "", m365: { ...empty.m365, ...(m365 || {}), client_secret: "" } });
      setPlaceholders(ph || []);
      setTemplates(tps || []);
      setActiveTpl(0);
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load email config"));
    api.get("/admin/email/logs").then((r) => setLogs(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/email/config", form);
      set("password_set", res.data.password_set); set("password", "");
      toast.success("Email configuration saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveTemplates = async () => {
    setBusy(true);
    try {
      await api.put("/admin/email/templates", { templates });
      toast.success(`${templates.length} template(s) saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Template save failed"); }
    finally { setBusy(false); }
  };

  const sendTest = async (providerOverride) => {
    if (!testTo) { toast.error("Enter a test email address"); return; }
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post("/admin/email/test", { to: testTo, provider: providerOverride || null });
      setTestResult(res.data);
      if (res.data.status === "sent") toast.success("Test email sent");
      else if (res.data.status === "skipped") toast.warning(`Skipped: ${res.data.failure_reason}`);
      else toast.error(`Failed: ${res.data.failure_reason}`);
      api.get("/admin/email/logs").then((r) => setLogs(r.data.items || [])).catch(() => {});
    } catch (e) {
      const d = e.response?.data?.detail;
      setTestResult({ status: "failed", failure_reason: typeof d === "string" ? d : (e.message || "Request failed") });
      toast.error(typeof d === "string" ? d : "Test send failed");
    }
    finally { setTesting(false); }
  };

  const tpl = templates[activeTpl] || null;
  const setTpl = (k, v) => setTemplates((ts) => ts.map((t, i) => i === activeTpl ? { ...t, [k]: v } : t));

  return (
    <div className="space-y-4" data-testid="email-config-page">
      <PageHeader title="Email Configuration" subtitle="SMTP settings, templates and send logs — emails go out in the background and are always logged" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Provider selector */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-provider-card">
            <div className="mb-2 text-sm font-semibold text-teal-700">Active Provider</div>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="email-provider" value={form.provider} onChange={(e) => set("provider", e.target.value)} className="h-9 rounded-md border bg-white px-2 text-sm">
                <option value="smtp">SMTP (classic)</option>
                <option value="m365">Microsoft 365 (Graph)</option>
              </select>
              {form.provider === "m365" && !form.m365?.complete && <Badge className="bg-amber-500">M365 config incomplete</Badge>}
              {form.m365_paused && <Badge className="bg-red-500">M365 auto-paused (circuit breaker)</Badge>}
              <span className="text-xs text-slate-400">Switching to Microsoft 365 requires a complete config + a successful test.</span>
            </div>
          </div>
          {/* SMTP settings */}
          <div className="rounded-xl border bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-teal-700">SMTP Settings</span>
              <label className="flex items-center gap-2 text-sm" data-testid="email-enabled-wrap">
                <span className={form.enabled ? "font-medium text-emerald-700" : "text-slate-400"}>{form.enabled ? "Email Enabled" : "Email Disabled"}</span>
                <Switch data-testid="email-enabled" checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} />
              </label>
            </div>
            {!form.enabled && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">While disabled, no mail goes out — every send is logged as "skipped" instead of failing.</p>}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <div className="col-span-2"><Label className="text-xs text-slate-500">SMTP Host</Label><Input data-testid="email-host" value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.gmail.com" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">SMTP Port</Label><Input data-testid="email-port" type="number" value={form.port} onChange={(e) => set("port", e.target.value)} className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Encryption</Label>
                <select data-testid="email-encryption" value={form.encryption} onChange={(e) => set("encryption", e.target.value)} className="mt-1 h-9 w-full rounded-md border bg-white px-2 text-sm">
                  {ENC_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>
              <div><Label className="text-xs text-slate-500">Username</Label><Input data-testid="email-username" value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="SMTP username" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Password {form.password_set && <Badge className="ml-1 bg-emerald-100 text-emerald-700">set</Badge>}</Label>
                <Input data-testid="email-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={form.password_set ? "•••••• (blank = keep existing)" : "SMTP password"} className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">From Name</Label><Input data-testid="email-from-name" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="MeterFlow ERP" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">From Email</Label><Input data-testid="email-from-email" value={form.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="noreply@company.com" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Reply-To (optional)</Label><Input data-testid="email-reply-to" value={form.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="support@company.com" className="mt-1 h-9" /></div>
              <div className="col-span-2 md:col-span-3"><Label className="text-xs text-slate-500">Default CC list (comma-separated, added to every outgoing mail)</Label>
                <Input data-testid="email-default-cc" value={form.default_cc} onChange={(e) => set("default_cc", e.target.value)} placeholder="audit@company.com, manager@company.com" className="mt-1 h-9" /></div>
            </div>
            <div className="mt-3 flex justify-end"><Button data-testid="email-save" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? "Saving…" : "Save Configuration"}</Button></div>
          </div>

          {/* Microsoft 365 settings */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-m365">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-teal-700">Microsoft 365 (Graph API)</span>
              {form.m365?.complete && <Badge className="bg-emerald-100 text-emerald-700">config complete</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-2">
              <div><Label className="text-xs text-slate-500">Tenant ID (Directory ID)</Label><Input data-testid="m365-tenant" value={form.m365?.tenant_id || ""} onChange={(e) => setM("tenant_id", e.target.value)} className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Client ID (Application ID)</Label><Input data-testid="m365-client" value={form.m365?.client_id || ""} onChange={(e) => setM("client_id", e.target.value)} className="mt-1 h-9" /></div>
              <div className="col-span-2"><Label className="text-xs text-slate-500">Client Secret {form.m365?.client_secret_set && <Badge className="ml-1 bg-emerald-100 text-emerald-700">set</Badge>}</Label>
                <Input data-testid="m365-secret" type="password" value={form.m365?.client_secret || ""} onChange={(e) => setM("client_secret", e.target.value)} placeholder={form.m365?.client_secret_set ? "•••••• (blank = keep existing)" : "Client secret value"} autoComplete="new-password" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Sender Email (licensed mailbox)</Label><Input data-testid="m365-sender-email" value={form.m365?.sender_email || ""} onChange={(e) => setM("sender_email", e.target.value)} placeholder="no-reply@yourdomain.com" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-500">Sender Name</Label><Input data-testid="m365-sender-name" value={form.m365?.sender_name || ""} onChange={(e) => setM("sender_name", e.target.value)} placeholder="MeterFlow ERP" className="mt-1 h-9" /></div>
              <div className="col-span-2"><Label className="text-xs text-slate-500">Reply-To (optional)</Label><Input data-testid="m365-reply-to" value={form.m365?.reply_to || ""} onChange={(e) => setM("reply_to", e.target.value)} className="mt-1 h-9" /></div>
            </div>
            <p className="mt-2 text-xs text-slate-400">Uses OAuth 2.0 client-credentials + Graph <code>/users/&#123;sender&#125;/sendMail</code>. Secret is encrypted at rest and never returned. Save here, then use "Test Microsoft 365" before switching the provider.</p>
            <div className="mt-3 flex justify-end"><Button data-testid="email-save-m365" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? "Saving…" : "Save Configuration"}</Button></div>
          </div>

          {/* Templates */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-templates">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-teal-700">Email Templates</span>
              <div className="flex gap-2">
                <Button data-testid="tpl-add" size="sm" variant="outline" onClick={() => { setTemplates((ts) => [...ts, { name: `Template ${ts.length + 1}`, subject: "", html_body: "" }]); setActiveTpl(templates.length); }}><Plus className="mr-1 h-3.5 w-3.5" />Add Template</Button>
                <Button data-testid="tpl-save" size="sm" onClick={saveTemplates} disabled={busy} className="bg-teal-700 hover:bg-teal-800">Save Templates</Button>
              </div>
            </div>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {templates.map((t, i) => (
                <button key={i} data-testid={`tpl-tab-${i}`} onClick={() => setActiveTpl(i)}
                  className={`rounded-full border px-3 py-1 text-xs ${i === activeTpl ? "border-teal-600 bg-teal-50 font-medium text-teal-700" : "text-slate-500 hover:bg-slate-50"}`}>{t.name || `Template ${i + 1}`}</button>
              ))}
            </div>
            {tpl && (
              <div className="space-y-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1"><Label className="text-xs text-slate-500">Template Name</Label><Input data-testid="tpl-name" value={tpl.name} onChange={(e) => setTpl("name", e.target.value)} className="mt-1 h-9" /></div>
                  {templates.length > 1 && <Button data-testid="tpl-delete" size="sm" variant="outline" className="h-9 border-red-200 text-red-600" onClick={() => { const ts = templates.filter((_, i) => i !== activeTpl); setTemplates(ts); setActiveTpl(0); }}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
                <div><Label className="text-xs text-slate-500">Subject (supports {"{{placeholders}}"})</Label><Input data-testid="tpl-subject" value={tpl.subject} onChange={(e) => setTpl("subject", e.target.value)} placeholder="Invoice {{invoice_number}} for {{customer_name}}" className="mt-1 h-9" /></div>
                <div><Label className="text-xs text-slate-500">HTML Body (supports {"{{placeholders}}"})</Label>
                  <Textarea data-testid="tpl-body" value={tpl.html_body} onChange={(e) => setTpl("html_body", e.target.value)} rows={7} placeholder="<p>Hello {{customer_name}},</p>..." className="mt-1 font-mono text-xs" /></div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Test email */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-test-card">
            <div className="mb-2 text-sm font-semibold text-teal-700">Send Test Email</div>
            <Input data-testid="email-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com" className="h-9" />
            <Button data-testid="email-test-send" size="sm" onClick={() => sendTest()} disabled={testing} className="mt-2 w-full bg-teal-700 hover:bg-teal-800">
              {testing ? <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />}Send Test ({form.provider === "m365" ? "Microsoft 365" : "SMTP"})</Button>
            <Button data-testid="email-test-m365" size="sm" variant="outline" onClick={() => sendTest("m365")} disabled={testing} className="mt-2 w-full">Test Microsoft 365 only</Button>
            {testResult && (
              <div data-testid="email-test-result" className={`mt-2 rounded-lg px-3 py-2 text-xs break-all ${testResult.status === "sent" ? "bg-emerald-50 text-emerald-700" : testResult.status === "skipped" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                <b>{testResult.status.toUpperCase()}</b>{testResult.failure_reason ? ` — ${testResult.failure_reason}` : ""}
              </div>
            )}
          </div>

          {/* Placeholder help */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-placeholders">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-teal-700"><Info className="h-4 w-4" />Available Placeholders</div>
            <p className="mb-2 text-xs text-slate-500">Use in template Subject / HTML Body as {"{{name}}"}. Rendered at send time.</p>
            <div className="space-y-1">
              {placeholders.map((p) => (
                <div key={p.name} className="flex items-baseline gap-2 text-xs">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-teal-700">{`{{${p.name}}}`}</code>
                  <span className="text-slate-500">{p.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Logs */}
          <div className="rounded-xl border bg-white p-4" data-testid="email-logs">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-teal-700"><Mail className="h-4 w-4" />Send Log</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={load} data-testid="email-logs-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="max-h-72 space-y-1.5 overflow-auto">
              {logs.map((l) => (
                <div key={l.id} data-testid={`email-log-${l.id}`} className="rounded-lg border px-2.5 py-1.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{(l.to || []).join(", ")}</span>
                    <Badge className={l.status === "sent" ? "bg-emerald-600" : l.status === "skipped" ? "bg-amber-500" : "bg-red-500"}>{l.status}</Badge>
                  </div>
                  <div className="truncate text-slate-500">{l.subject}</div>
                  {l.failure_reason && <div className="truncate text-red-500">{l.failure_reason}</div>}
                  <div className="text-[10px] text-slate-400">{(l.created_at || "").replace("T", " ").slice(0, 19)}</div>
                </div>
              ))}
              {!logs.length && <p className="py-4 text-center text-xs text-slate-400">No emails attempted yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
